import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useIsHydrated } from "../lib/use-is-hydrated.js";
import { getClaims } from "./utils.js";

export type WidgetType =
  | "admin-portal-audit-log-streaming"
  | "admin-portal-domain-verification"
  | "user-management"
  | "organization-switcher"
  | "user-sessions"
  | "user-security"
  | "user-profile"
  | "admin-portal-sso-connection"
  | "api-keys"
  | "pipes"
  | "directory-sync";

export type AuthToken = string | (() => Promise<string>);

interface ElevatedAccess {
  token: string;
  expiresAt: string;
}

interface ApiConfig {
  authToken: string | undefined;
  permissions: string[] | undefined;
  exp: number | undefined;
  baseUrl: string;
  widgetType: WidgetType;
  elevatedAccess?: ElevatedAccess;
  setElevatedAccess: (elevatedAccess?: ElevatedAccess) => void;
}

export interface AuthTokenQueryData {
  authToken: string;
  permissions: string[] | undefined;
  exp: number | undefined;
}

export const authTokenQueryKey = (widgetType: WidgetType) =>
  ["authToken", widgetType] as const;

// Refetch this many ms before the JWT expires so data queries always see a
// fresh token.
const TOKEN_REFRESH_BUFFER_MS = 30_000;

// Cap setTimeout delays at ~24 days to stay well below the 32-bit signed int
// limit (~24.8 days), which some runtimes silently coerce to 1ms.
const MAX_TIMEOUT_MS = 2_147_483_000;

const ApiContext = React.createContext<ApiConfig | undefined>(undefined);

interface ApiProviderProps {
  authToken: AuthToken;
  baseUrl: string;
  children: React.ReactNode;
  widgetType: WidgetType;
}

export const ApiProvider = ({
  authToken,
  baseUrl,
  children,
  widgetType,
}: ApiProviderProps) => {
  const queryClient = useQueryClient();
  const authTokenQuery = useQuery<AuthTokenQueryData>({
    initialData: () => {
      if (typeof authToken !== "string") {
        return undefined;
      }

      const claims = getClaims(authToken);
      return {
        authToken,
        permissions: claims.permissions,
        exp: claims.exp,
      };
    },
    queryFn: async () => {
      const resolvedToken =
        typeof authToken === "string" ? authToken : await authToken();
      const claims = getClaims(resolvedToken);

      return {
        authToken: resolvedToken,
        permissions: claims.permissions,
        exp: claims.exp,
      };
    },
    queryKey: authTokenQueryKey(widgetType),
  });

  // Keep the token "fresh" until ~30s before it expires, so window-focus
  // re-fetches re-mint before data queries use a stale Bearer.
  //
  // TODO: prefer the function-form `staleTime` on `useQuery` above once we can
  // bump the `@tanstack/react-query` peer dep to ^5.44.0 (the version that
  // introduced it). That lets react-query own the freshness window and avoids
  // the extra timer + window-focus nonsense this effect introduces.
  const exp = authTokenQuery.data?.exp;
  React.useEffect(() => {
    if (!exp) {
      return;
    }

    const delay = Math.min(
      MAX_TIMEOUT_MS,
      exp * 1000 - Date.now() - TOKEN_REFRESH_BUFFER_MS,
    );
    if (delay <= 0) {
      void queryClient.refetchQueries({
        queryKey: authTokenQueryKey(widgetType),
      });
      return;
    }
    const timeout = window.setTimeout(() => {
      void queryClient.refetchQueries({
        queryKey: authTokenQueryKey(widgetType),
      });
    }, delay);
    return () => window.clearTimeout(timeout);
  }, [exp, queryClient, widgetType]);

  const [elevatedAccess, setElevatedAccess] = React.useState<ElevatedAccess>();
  const elevatedAccessTimeout = React.useRef<number | undefined>(undefined);

  // This effect manages the expiration of elevated access tokens
  // When an elevated access token is present, it checks every 30 seconds if the token has expired
  // If the token has expired (current time > expiration time), it clears the elevated access
  React.useEffect(() => {
    if (elevatedAccessTimeout.current) {
      window.clearInterval(elevatedAccessTimeout.current);
    }

    if (elevatedAccess) {
      elevatedAccessTimeout.current = window.setInterval(() => {
        const now = new Date();
        const expiresAtDate = new Date(elevatedAccess.expiresAt);

        //  Reset the elevated access if it has expired
        if (now > expiresAtDate) {
          setElevatedAccess(undefined);
        }
      }, 30_000); // every 30 seconds
    }

    return () => {
      if (elevatedAccessTimeout.current) {
        window.clearInterval(elevatedAccessTimeout.current);
      }
    };
  }, [elevatedAccess]);

  const value = React.useMemo(
    () => ({
      authToken: authTokenQuery.data?.authToken,
      permissions: authTokenQuery.data?.permissions,
      exp: authTokenQuery.data?.exp,
      baseUrl,
      elevatedAccess,
      setElevatedAccess,
      widgetType,
    }),
    [
      authTokenQuery.data,
      baseUrl,
      elevatedAccess,
      setElevatedAccess,
      widgetType,
    ],
  );

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};

export const useApi = () => {
  const context = React.useContext(ApiContext);

  if (context === undefined) {
    throw new Error("useApi must be used within an ApiProvider");
  }

  return context;
};

export const useElevatedAccessToken = () => {
  const { elevatedAccess, setElevatedAccess } = useApi();

  return { elevatedAccess, setElevatedAccess };
};

export const useApiReady = () => {
  const { authToken } = useApi();
  const isHydrated = useIsHydrated();

  return isHydrated && authToken !== undefined;
};
